import { FC, useCallback, useEffect, useState } from 'react';
import { styled } from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { useStore } from 'common/store';
import { UseStoreValues } from 'common/types/store';
import {
  DISABLED_TOOLTIP,
  FOUR_O_NINE_A_REPORT,
  NO_INFORMATION_AVAILABLE_FOR_REPORT_DATE,
} from 'dashboard409a/common/constants';
import { overviewTourSteps } from 'dashboard409a/common/constants/tours/index';
import { Collapse, TourButton, TourGuide, Valuation, VisibilityToggle, Waterfall } from 'dashboard409a/components';
import { AccordionReportHeader } from 'dashboard409a/components/AccordionReportHeader';
import { Disclaimer } from 'dashboard409a/components/Disclaimer';
import { PublicComps } from 'dashboard409a/components/PublicComps';
import { VisibilityToggleType } from 'dashboard409a/components/VisibilityToggle/VisibilityToggle.props';
import { useCompanyDashboardContext } from 'dashboard409a/context/CompanyDashboardContext/CompanyDashboardContext';
import { useTourGuide } from 'dashboard409a/hooks/useTourGuide';
import { IValuationState } from 'dashboard409a/states/features/valuation';
import { IWaterfallState, loadWaterfall, toggleWaterfallVisibility } from 'dashboard409a/states/features/waterfall';
import { AppDispatchType, RootStateType } from 'dashboard409a/states/store';
import EmptyReportDataMessage from './EmptyReportDataMessage';

// Temporary types for React 19 compatibility
// TODO: Replace with types from a React 19 compatible tour library
interface CallBackProps {
  action: string;
  index: number;
  status: string;
  type: string;
}

const PREFIX = 'Overview';

const classes = {
  waterfallTitle: `${PREFIX}-waterfallTitle`,
};

// TODO jss-to-styled codemod: The Fragment root was replaced by div. Change the tag if needed.
const Root = styled('div')(() => ({
  [`& .${classes.waterfallTitle}`]: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    width: '100%',
  },
}));

const Overview: FC = () => {
  const [isValuationCollapseOpen, setIsValuationCollapseOpen] = useState(true);
  const { currentCompany } = useSelector((state: RootStateType) => state.global);
  const [{ user }] = useStore() as unknown as UseStoreValues;
  const { editMode } = useCompanyDashboardContext();

  const isSuperuser = user?.is_superuser;
  const isStaff = user?.is_staff;
  const isAnalyst = isSuperuser || isStaff;

  const dispatch = useDispatch<AppDispatchType>();
  const { run, stepIndex, startTour, handleJoyrideCallback } = useTourGuide();

  const handleTourCallback = useCallback(
    (data: CallBackProps) => {
      handleJoyrideCallback(data);
    },
    [handleJoyrideCallback]
  );

  const { currentMeasurementDateId } = useSelector((state: RootStateType) => state.global);
  const { waterfallBreakpoint, isWaterfallVisible, currentCaptable } = useSelector<RootStateType, IWaterfallState>(
    state => state.waterfall
  );
  const { currentValuationScalarOpinion } = useSelector<RootStateType, IValuationState>(state => state.valuation);

  const isApprovedForClients = currentValuationScalarOpinion?.approved_for_clients ?? false;
  const isCompanyUserAndIsNotApprovedForClients = !isSuperuser && !isStaff && !isApprovedForClients;
  const shouldShowWaterfall = waterfallBreakpoint && (isAnalyst || isWaterfallVisible);

  // Custom tour starter that handles collapse opening
  const handleStartTour = useCallback(() => {
    const hasValuationTableStep = overviewTourSteps.some(
      step => step.target === '[data-tour="valuation-methods-detail-table"]'
    );
    let needsDelay = false;

    if (hasValuationTableStep) {
      setIsValuationCollapseOpen(true);
      needsDelay = true;
    }

    if (needsDelay) {
      setTimeout(() => {
        startTour();
      }, 350);
    } else {
      // No collapses to open, start tour immediately
      startTour();
    }
  }, [startTour]);

  const handleToggleWaterfallVisibility = useCallback(
    (visible: boolean) => {
      if (currentCaptable?.id) {
        dispatch(
          toggleWaterfallVisibility({
            captableId: currentCaptable.id,
            visible,
          })
        );
      }
    },
    [dispatch, currentCaptable]
  );

  const renderNoReportDataMessage = () => <EmptyReportDataMessage title={NO_INFORMATION_AVAILABLE_FOR_REPORT_DATE} />;

  useEffect(() => {
    if (currentCompany?.id && currentMeasurementDateId) {
      dispatch(
        loadWaterfall({
          companyId: currentCompany.id,
          measurementDateId: currentMeasurementDateId,
        })
      );
    }
  }, [currentCompany?.id, currentMeasurementDateId, dispatch]);

  if (!currentValuationScalarOpinion?.report_date) {
    return renderNoReportDataMessage();
  }

  return (
    <Root>
      <TourGuide run={run} stepIndex={stepIndex} steps={overviewTourSteps} onCallback={handleTourCallback} />
      {isAnalyst && editMode && <TourButton onStartTour={handleStartTour} />}
      {/* Page-level report header, above the section cards (redesign). */}
      <AccordionReportHeader reportName={FOUR_O_NINE_A_REPORT} />
      <Collapse
        title="Valuation"
        disabled={isCompanyUserAndIsNotApprovedForClients}
        disabledTooltip={DISABLED_TOOLTIP}
        isOpen={isValuationCollapseOpen}
        onToggle={setIsValuationCollapseOpen}>
        <Valuation />
      </Collapse>
      <Collapse
        title="Public Comps"
        disabled={isCompanyUserAndIsNotApprovedForClients}
        disabledTooltip={DISABLED_TOOLTIP}>
        <PublicComps />
      </Collapse>
      {shouldShowWaterfall && (
        <>
          {isAnalyst && editMode && (
            <div data-tour="waterfall-visibility-toggle" style={{ display: 'flex', justifyContent: 'flex-end' }}>
              <VisibilityToggle
                visible={isWaterfallVisible}
                onChange={handleToggleWaterfallVisibility}
                tooltipTitle="Toggle visibility for company users"
                type={VisibilityToggleType.BAR}
              />
            </div>
          )}
          <Collapse
            title={<div className={classes.waterfallTitle}>Waterfall</div>}
            disabled={isCompanyUserAndIsNotApprovedForClients}
            disabledTooltip={DISABLED_TOOLTIP}>
            <Waterfall />
          </Collapse>
        </>
      )}
      <Disclaimer />
    </Root>
  );
};

export default Overview;
